import { cn } from '@cannect/lib/utils'
import { Button, Form, ScrollArea, Separator, Sheet, Typography } from '@cannect/new-components/atoms'
import { CustomSelect } from '@cannect/new-components/atoms/Form/CustomSelect'
import { CounterBadge } from '@cannect/new-components/molecules/CounterBadge'
import { ProductFilters, ProductListItem } from '@cannect/new-components/organisms'
import { Menu, Transition } from '@headlessui/react'
import EmptyState from 'components/EmptyState'
import LoadingContainer from 'components/LoadingContainer'
import Pagination from 'components/Pagination'
import { Check, ChevronDown } from 'lucide-react'
import { Fragment, useMemo } from 'react'
import { LuGrid, LuList, LuSearch } from 'react-icons/lu'
import When from 'utils/when'

import { useProductLayout } from '@cannect/new-components/organisms/ProductListItem/useProductLayout'
import { TProductFiltersByNames } from '@cannect/services/resources/products'
import { Link, useLocation } from 'react-router-dom'
import { ORDER_SELECT_OPTIONS } from '../ProductsList/constants'
import { useProductsList } from '../ProductsList/useProductsList'
import { BrandBanner } from './BrandBanner'

type TBrandProductsListUI = ReturnType<typeof useProductsList>

const LayoutDropdown = ({
  currentLayout,
  onChange
}: {
  currentLayout: 'grid' | 'list'
  onChange: (value: 'grid' | 'list') => void
}) => {
  const layoutOptions = [
    { value: 'grid', label: 'Em Grade', icon: LuGrid },
    { value: 'list', label: 'Em Lista', icon: LuList }
  ]

  const selected = layoutOptions.find((opt) => opt.value === currentLayout)

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="relative inline-flex h-9 w-[158px] items-center justify-between rounded-md bg-white px-2 py-2 text-sm font-normal text-neutral-700 hover:bg-gray-50"
        style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}>
        <span className="flex items-center text-neutral-500">{selected?.icon && <selected.icon size={16} />}</span>
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-neutral-500">
          {selected?.label}
        </span>
        <ChevronDown size={16} className="text-neutral-500" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95">
        <Menu.Items
          className="absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white text-neutral-500 shadow-lg focus:outline-none"
          style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}>
          {layoutOptions.map((option) => (
            <Menu.Item key={option.value}>
              {({ active }) => (
                <button
                  onClick={() => onChange(option.value as 'grid' | 'list')}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 border-0 px-4 py-2 text-sm text-neutral-500',
                    active && 'bg-gray-100'
                  )}>
                  <div className="flex items-center gap-2">
                    <option.icon size={16} />
                    {option.label}
                  </div>
                  {currentLayout === option.value && <Check size={14} className="text-primary-600" />}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export const BrandProductsListUI = ({
  products,
  filters,
  totalCount,
  totalPages,
  currentPage,
  isLoading,
  isFetching,
  isFlowerBannerVisible,
  search,
  currentSortOrder,
  currentLayout,
  isAuthenticated,
  isMobile,
  activeFiltersCount,
  handleSearchChange,
  handlePageChange,
  handleSortChange,
  handleLayoutChange
}: TBrandProductsListUI) => {
  const { layout } = useProductLayout()
  const location = useLocation()

  const { brandId: _brandId, brandLabel: _brandLabel } = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const id = params.get('brand_id') || ''
    const options = (filters as any)?.brand_id || []
    const match = options.find?.((opt: any) => String(opt?.value) === String(id))
    return {
      brandId: id,
      brandLabel: match?.label || null
    }
  }, [location.search, filters])

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {isFlowerBannerVisible && (
        <div className="mb-6 border-l-4 border-yellow-500 bg-yellow-100 p-4">
          <Typography.Text className="text-yellow-800">
            ⚠️ Produtos de flores disponíveis - Entre em contato via WhatsApp
          </Typography.Text>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <BrandBanner />
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            {isMobile ? (
              <Sheet.Root modal={false}>
                <Sheet.Trigger asChild>
                  <Button variant="outline" className="relative w-full gap-2">
                    <span>Filtros</span>
                    {activeFiltersCount > 0 && <CounterBadge quantity={activeFiltersCount} />}
                  </Button>
                </Sheet.Trigger>

                <Sheet.Content onInteractOutside={(e) => e.preventDefault()} className="pb-[120px]">
                  <Sheet.Header className="mt-8">
                    <Sheet.Title>Filtros de Produtos</Sheet.Title>
                    <Sheet.Description>Refine a busca pelos produtos dessa marca</Sheet.Description>
                  </Sheet.Header>

                  <Separator className="my-4" />

                  <ScrollArea className="h-[calc(100%-120px)] pr-2">
                    <ProductFilters filters={filters as TProductFiltersByNames} />
                  </ScrollArea>

                  <Sheet.Footer className="h-[120px] items-center justify-end gap-3 px-6 py-4">
                    <Sheet.Close asChild>
                      <Button size="lg" full>
                        Aplicar Filtros
                      </Button>
                    </Sheet.Close>
                  </Sheet.Footer>
                </Sheet.Content>
              </Sheet.Root>
            ) : (
              <ProductFilters filters={filters as TProductFiltersByNames} />
            )}
          </div>

          <div className="lg:col-span-3">
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-center md:gap-4 md:pb-2">
              <Typography.Text className="[&&]:text-gray-600">
                Não encontrou seu produto?{' '}
                <Link to="/contato" className="[&&]:text-primary-800">
                  Envie aqui sua receita e documentos.
                </Link>
              </Typography.Text>

              <Typography.Text className="text-sm text-gray-500">
                {products.length} de {totalCount} resultado{totalCount > 1 ? 's' : ''}
              </Typography.Text>
            </div>

            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="w-2/3 flex-1">
                <Form.Input
                  size="md"
                  className="rounded-full bg-white"
                  placeholder="Buscar produtos..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  variant="search"
                  rigthElement={<LuSearch />}
                />
              </div>

              <div className="flex w-fit flex-col gap-2 sm:flex-row sm:items-center">
                <CustomSelect
                  size="small"
                  placeholder="Ordenar por"
                  options={ORDER_SELECT_OPTIONS.filter((option) => !option.enableWithLogin || isAuthenticated)}
                  value={ORDER_SELECT_OPTIONS.find((option) => option.value === currentSortOrder) || null}
                  onChange={handleSortChange}
                  customClassNames={{
                    container: 'w-full'
                  }}
                />

                {!isMobile && isAuthenticated && (
                  <LayoutDropdown
                    currentLayout={currentLayout as 'grid' | 'list'}
                    onChange={(value) => handleLayoutChange({ value: value as 'grid' | 'list' })}
                  />
                )}
              </div>
            </div>

            <When expr={isLoading}>
              <LoadingContainer loading />
            </When>

            <When expr={!isLoading && products.length === 0}>
              <EmptyState title="Nenhum produto encontrado" withAnimation />
            </When>

            <When expr={!isLoading && products.length > 0}>
              <div className="mb-8" data-product-sections>
                <div
                  className={`justify-items-center gap-x-4 gap-y-4 ${
                    layout === 'grid' ? 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4' : 'flex flex-col'
                  }`}>
                  {products.map((product: any) => (
                    <ProductListItem key={product.id} product={product} variant={layout} />
                  ))}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center pt-4">
                  <Pagination
                    containerClassName="[&&]:justify-end"
                    forcePage={currentPage - 1}
                    onPageChange={({ selected }: any) => handlePageChange(selected + 1)}
                    pageCount={totalPages}
                  />
                </div>
              )}
            </When>

            {isFetching && !isLoading && (
              <div className="flex justify-center py-4">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrandProductsListUI
